
import { Avatar, Select, Spin, message, Button } from "antd";
import { CloseOutlined, InfoCircleOutlined, LinkOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../../../../../core/axios/axiosInstance";
import { changeContactStatus, updateContactTags } from "../../../../../core/api/contactApi";
import { fetchTags } from "../../../../../core/data/redux/slices/TagSlice";

const ContactDetails = ({ contact, onClose }) => {
  const dispatch        = useDispatch();
  const navigate        = useNavigate();
  const contactStatuses = useSelector((s) => s.profile.contactStatuses) || [];
  const leadStatuses    = useSelector((s) => s.profile.leadStatuses)    || [];
  const allTags         = useSelector((s) => s.tags.tags) || [];
  const userRole        = useSelector((s) => s.profile.role);

  const [crmRecord, setCrmRecord]       = useState(null); // { type, contactId, status, tags }
  const [loadingCrm, setLoadingCrm]     = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingTags, setSavingTags]     = useState(false);
  const [showNameTip, setShowNameTip]   = useState(false);

  useEffect(() => {
    if (allTags.length === 0) dispatch(fetchTags());
  }, []);

  useEffect(() => {
    if (!contact?.from) return;
    setCrmRecord(null);

    const phone = contact.from;
    setLoadingCrm(true);

    api.get("/getAllContactsOrLeads/searchByPhone", { params: { phone } })
      .then((res) => {
        const results = res.data?.results || [];
        if (results.length > 0) {
          const r = results[0];
          setCrmRecord({
            type:      r.type,
            contactId: r.contactId,
            status:    r.status    || "",
            tags:      r.tags      || [],
            firstname: r.firstname || "",
            lastname:  r.lastname  || "",
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCrm(false));
  }, [contact?.from]);

  const handleStatusChange = async (newStatus) => {
    if (!crmRecord) return;
    setSavingStatus(true);
    try {
      await changeContactStatus({
        contact_id: crmRecord.contactId,
        newStatus,
        category: crmRecord.type,
      });
      setCrmRecord((prev) => ({ ...prev, status: newStatus }));
      message.success("Status updated");
    } catch {
      message.error("Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleTagsChange = async (selectedTagIds) => {
    if (!crmRecord) return;
    setSavingTags(true);
    try {
      const newTags = allTags
        .filter((t) => selectedTagIds.includes(t.tag_id))
        .map((t) => ({ tag: t.tag, emoji: t.emoji, tag_id: t.tag_id }));

      await updateContactTags({
        contact_id: crmRecord.contactId,
        tags: newTags,
        category: crmRecord.type,
      });

      setCrmRecord((prev) => ({
        ...prev,
        tags: allTags.filter((t) => selectedTagIds.includes(t.tag_id)),
      }));
      message.success("Groups updated");
    } catch {
      message.error("Failed to update groups");
    } finally {
      setSavingTags(false);
    }
  };

  const currentTagIds = (crmRecord?.tags || [])
    .map((t) => t.tag_id)
    .filter((id) => allTags.some((ut) => ut.tag_id === id));

  const tagOptions = allTags.map((t) => ({
    value: t.tag_id,
    label: `${t.emoji || ""} ${t.tag}`.trim(),
  }));

  const statusOptions = crmRecord?.type === "lead"
    ? leadStatuses.map((s) => ({ value: s.value, label: s.label }))
    : contactStatuses.map((s) => ({ value: s.value, label: s.label }));

  return (
    <div className="contact-details">
      <div className="contact-header">
        <span>Contact Info</span>
        <CloseOutlined onClick={onClose} />
      </div>

      <div className="contact-body">
        <Avatar src={contact.image} size={100}>
          {(contact?.chatName || contact?.originalName || contact?.from || "?")?.charAt(0)}
        </Avatar>

        <h3 style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
          {contact?.chatName || contact?.originalName || contact?.from || "Unknown"}
          {contact?.chatName && contact?.originalName && contact.chatName === contact.originalName && (
            <span style={{ position: "relative" }}>
              <InfoCircleOutlined
                style={{ fontSize: 14, color: "#9ca3af", cursor: "pointer" }}
                onClick={() => setShowNameTip((v) => !v)}
              />
              {showNameTip && (
                <span style={{
                  position: "absolute",
                  bottom: "calc(100% + 8px)",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#1f2937",
                  color: "#fff",
                  fontSize: 11,
                  padding: "5px 10px",
                  borderRadius: 6,
                  whiteSpace: "nowrap",
                  zIndex: 100,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  lineHeight: 1.4,
                }}>
                  This is the contact's WhatsApp profile name
                  {/* arrow */}
                  <span style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "5px solid transparent",
                    borderRight: "5px solid transparent",
                    borderTop: "5px solid #1f2937",
                  }} />
                </span>
              )}
            </span>
          )}
        </h3>
        <p>{contact.phone}</p>

        {crmRecord && (
          <Button
            size="small"
            icon={<LinkOutlined />}
            style={{ marginBottom: 8, fontSize: 12 }}
            onClick={() => {
              const record = {
                contact_id: crmRecord.contactId,
                id:         crmRecord.contactId,
                key:        crmRecord.contactId,
                firstname:  crmRecord.firstname,
                lastname:   crmRecord.lastname,
              };
              const route = crmRecord.type === "lead"
                ? (userRole === "user" ? "/agent/leads-details" : "/leads-details")
                : (userRole === "user" ? "/agent/crm/customer-details" : "/crm/customer-details");
              navigate(route, { state: { record } });
            }}
          >
            View in CRM
          </Button>
        )}

        {/* CRM Status */}
        <div className="contact-section">
          {loadingCrm ? (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <Spin size="small" />
            </div>
          ) : !crmRecord ? (
            <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>
              Number not saved in contacts or leads
            </p>
          ) : (
            <>
              <strong>
                {crmRecord.type === "lead" ? "Lead" : "Contact"} Status
              </strong>
              <Select
                value={crmRecord.status || undefined}
                onChange={handleStatusChange}
                options={statusOptions}
                placeholder="Change status"
                size="small"
                style={{ width: "100%", marginTop: 4 }}
                loading={savingStatus}
                disabled={savingStatus}
              />
            </>
          )}
        </div>

        {/* Groups / Tags */}
        {crmRecord && (
          <div className="contact-section">
            <strong>Groups</strong>
            <Select
              mode="multiple"
              value={currentTagIds}
              onChange={handleTagsChange}
              options={tagOptions}
              placeholder="Add to group"
              size="small"
              style={{ width: "100%", marginTop: 6 }}
              loading={savingTags}
              disabled={savingTags || loadingCrm}
              allowClear
              maxTagCount="responsive"
            />
          </div>
        )}

        <div className="contact-section">
          <strong>User's whatsapp profile name</strong>
          <p>{contact.originalName || "N.A"}</p>
        </div>
        <div className="contact-section">
          <strong>User Last Message</strong>
          <div>{contact.userActiveStatus || "Unknown"}</div>
at
          <div>{contact.lastIncomingTime
            ? new Date(contact.lastIncomingTime).toLocaleString()
            : "N/A"}
          </div>
        </div>

        <div className="contact-section">
          <strong>First Message</strong>
          <p>{contact.firstUserMessage || "No first message"}</p>
        </div>

        <div className="contact-section">
          <strong>Last Message Time</strong>
          <p>{contact.lastMessageTime
            ? new Date(contact.lastMessageTime).toLocaleString()
            : "N/A"}
          </p>
        </div>

        <div className="contact-section">
          <strong>Total Session Messages</strong>
          <p>{contact.sessionMessages || 0}</p>
        </div>

        <div className="contact-section">
          <strong>Template Messages</strong>
          <p>{contact.templateMessages || 0}</p>
        </div>
      </div>
    </div>
  );
};

export default ContactDetails;
